import { FaLock } from "react-icons/fa"
import { useCallback, useContext, useEffect } from "react"
import { useRecoilState, useRecoilValue } from "recoil"
import { Box, Flex, ListItem, OrderedList } from "@chakra-ui/layout"

import { Icon } from "../common/icons"
import { SDKContext } from "../../context/SDKContext"
import { brandingSelector } from "../../recoil/atoms/room"
import { showDevicePermissionOverlayAtom } from "../../recoil/atoms/settings"
import { useBrowserType } from "../../hooks/useBrowserType"
import CustomOverlayModal from "../common/CustomOverlayModal"
import { DevicePermissionState, DevicePermissionType } from "./settings.interfaces"
import { Trans, useTranslation } from "react-i18next"
import { IS_ANDROID_NATIVE_APP } from "src/constants"
import useConfig from "src/hooks/useConfig"

const InformationForChrome = ({ deviceName }) => {
  const { t } = useTranslation()
  const roomStyles = useRecoilValue(brandingSelector)

  const LockIcon = () => ( <Icon
    src={FaLock}
    fontSize="0.8rem"
    marginLeft="1px"
    color={roomStyles?.servicePanelElementPrimaryText ?? "#000"}
  />)

  return (
    <OrderedList>
       <ListItem>
       <Trans
          i18nKey="Click the <0> </0>  lock in your browser's address bar."
          components={[<LockIcon />]}
          />
      </ListItem>
      <ListItem>{t("Turn on your {{deviceName}}", { deviceName })}</ListItem>
    </OrderedList>
  )
}

const DevicePermissionOverlay = () => {
  const config = useConfig()
  const meetingClient = useContext(SDKContext)
  const { t } = useTranslation()
  const { isChrome, isSafariDesktop } = useBrowserType()
  const [showDevicePermissionOverlay, setShowDevicePermission] = useRecoilState(
    showDevicePermissionOverlayAtom
  )

  const onCancel = useCallback(() => {
    setShowDevicePermission(null)
  }, [setShowDevicePermission])

  useEffect(() => {
    // Start listening Device permission, which can used to stop camera when permission denied
    meetingClient?.startListeningDevicePermission()
  }, [meetingClient])

  if (!showDevicePermissionOverlay) return null

  const { type, state } = showDevicePermissionOverlay
  const deviceName =
    (type === DevicePermissionType.camera ? t('Camera') : t("Microphone")).toLowerCase()

  let message: string | React.ReactNode = ""
  if (state === DevicePermissionState.prompt) {
    message = isSafariDesktop ? (
      t("PermissionDeniedMessageForIOS", { deviceName })
    ) : (
      t("PermissionPromptFallbackMessage", { deviceName })
    )
  } else {
    if (IS_ANDROID_NATIVE_APP) {
      message = t("PermissionDeniedForAndroidNative", { deviceName })
    } else if (isChrome) {
      message = <InformationForChrome deviceName={ deviceName } />
    } else {
      message = t("PermissionDeniedFallbackMessage", { deviceName })
    }
  }

  return (
    <CustomOverlayModal
      title={t("PermissionDeniedOverlayTitle", { 
        platformName: config.platformName ?? "LiveSell", 
        deviceName 
      })}
      titleStyles={{
        textAlign: "left",
        fontSize: "1.2rem"
      }}
      renderContent={() => (
        <Flex marginBottom="0.4rem" marginTop="0.4rem">
          <Box>{message}</Box>
        </Flex>
      )}
      onCancel={onCancel}
      showActionButton={false}
    />
  )
}

export default DevicePermissionOverlay
